import { getJson } from "../../api";

const BATCH_DELAY_MS = 40;
const batches = new Map();
const cache = new Map();

function cacheKey(apiUrl, spnId) {
  return `${apiUrl}::${spnId}`;
}

function buildBatchUrl(apiUrl, countIds, compatibilityIds) {
  const url = new URL(apiUrl, window.location.origin);

  if (countIds.length > 0) {
    url.searchParams.set("count_ids", countIds.join(","));
  }

  if (compatibilityIds.length > 0) {
    url.searchParams.set("compatibility_ids", compatibilityIds.join(","));
  }

  return url.toString();
}

async function flushBatch(apiUrl) {
  const batch = batches.get(apiUrl);
  if (!batch) {
    return;
  }

  batches.delete(apiUrl);
  const entries = Array.from(batch.entries.values());
  const countIds = entries.filter((entry) => entry.needCount).map((entry) => entry.spnId);
  const compatibilityIds = entries.filter((entry) => entry.needCompatibility).map((entry) => entry.spnId);

  try {
    const payload = await getJson(buildBatchUrl(apiUrl, countIds, compatibilityIds));
    const metrics = payload?.data?.metrics;
    if (!payload?.ok || !metrics || typeof metrics !== "object") {
      throw new Error(payload?.message || "Nie udało się pobrać metryk SPN.");
    }

    for (const entry of entries) {
      const metric = metrics[String(entry.spnId)];
      if (!metric || typeof metric !== "object") {
        const error = new Error("Nie znaleziono metryk wskazanego SPN.");
        entry.rejecters.forEach((reject) => reject(error));
        continue;
      }

      const key = cacheKey(apiUrl, entry.spnId);
      const cachedMetric = { ...(cache.get(key) || {}), ...metric };
      cache.set(key, cachedMetric);
      entry.resolvers.forEach((resolve) => resolve(cachedMetric));
    }
  } catch (error) {
    for (const entry of entries) {
      entry.rejecters.forEach((reject) => reject(error));
    }
  }
}

export function getSpnMetrics(apiUrl, spnId, options = {}) {
  const normalizedId = Number.parseInt(String(spnId || "0"), 10) || 0;
  const needCount = options.count === true;
  const needCompatibility = options.compatibility === true;

  if (!apiUrl || normalizedId <= 0 || (!needCount && !needCompatibility)) {
    return Promise.reject(new Error("Nieprawidłowe żądanie metryk SPN."));
  }

  const cachedMetric = cache.get(cacheKey(apiUrl, normalizedId));
  const hasCount = !needCount || Number.isFinite(cachedMetric?.studentCount);
  const hasCompatibility = !needCompatibility || typeof cachedMetric?.compatible === "boolean";
  if (cachedMetric && hasCount && hasCompatibility) {
    return Promise.resolve(cachedMetric);
  }

  let batch = batches.get(apiUrl);
  if (!batch) {
    batch = {
      entries: new Map(),
      timer: window.setTimeout(() => flushBatch(apiUrl), BATCH_DELAY_MS),
    };
    batches.set(apiUrl, batch);
  }

  let entry = batch.entries.get(normalizedId);
  if (!entry) {
    entry = {
      spnId: normalizedId,
      needCount: false,
      needCompatibility: false,
      resolvers: [],
      rejecters: [],
    };
    batch.entries.set(normalizedId, entry);
  }

  entry.needCount = entry.needCount || needCount;
  entry.needCompatibility = entry.needCompatibility || needCompatibility;

  return new Promise((resolve, reject) => {
    entry.resolvers.push(resolve);
    entry.rejecters.push(reject);
  });
}